import React, { useState, useEffect } from "react";
import { Modal, Box, Table, TableHead, TableRow, TableCell, TableBody, CircularProgress, Paper, Button } from "@mui/material";
import { useNavigate } from "react-router-dom";
import Despachos from "../api/despachos";
import { useProductStore } from "../hooks/useProductStore";
import MenuSection from "../components/MenuSection";
import { useTheme } from "@mui/material/styles";
import estadoDespacho from "../api/estadoDespacho";
import LocalGasStationIcon from "@mui/icons-material/LocalGasStation"; // Importar el icono
import apiErrorHandler from "../helpers/apiErrorHandler";
import { argbToCssColor } from "../helpers/colorUtils";
import combustible from "../api/getDespachosReal";

const DespachosSearchPage = () => {
  const [modalWidth, setModalWidth] = useState(window.innerWidth < 700 ? "360px" : "900px");
  const [despachoArray, setDespachoArray] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [shouldFetch, setShouldFetch] = useState(true);
  const { idProductoArray, addProducto } = useProductStore();
  const navigate = useNavigate();
  const theme = useTheme();
  const colorTexto = theme.bandera?.colorTextoBoton || "white";
  const cantidadAMostrar = parseInt(localStorage.getItem("cantidadAMostrar")) || 25; // Default a 25 si no existe o es 0
  const tiempoDeRefresco = localStorage.getItem("tiempoDeRefresco") * 1000;
  const esYPF = localStorage.getItem("bandera") === "ypf";

  const fetchDespachosPorPuesto = async () => {
    setIsLoading(true);
    try {
      const puesto = JSON.parse(localStorage.getItem("puesto"));
      const idPuesto = puesto?.idPuesto;

      if (!idPuesto) {
        throw new Error("No se encontró idPuesto en localStorage");
      }

      const respuesta = await combustible.obtenerPorPuesto(idPuesto);
      const data = respuesta.data.combustibles;

      // Tiempo máximo en segundos desde localStorage
      const tiempoDespachos = parseInt(localStorage.getItem("tiempoDespachos")); // En segundos
      const ahora = new Date();

      // Filtrar por tiempo válido - Si tiempoDespachos es 0, null, undefined o NaN, mostrar todos
      const despachosValidos = data.filter(d => {
        // Si tiempoDespachos es 0 o inválido, no filtrar por tiempo
        if (!tiempoDespachos || tiempoDespachos <= 0 || isNaN(tiempoDespachos)) {
          return true; // Mostrar todos los despachos
        }

        const horaDespacho = new Date(d.hora);
        const diferenciaEnSegundos = Math.abs((ahora - horaDespacho) / 1000); // Math.abs para manejar fechas futuras
        return diferenciaEnSegundos <= tiempoDespachos;
      });

      // Ordenar por hora descendente (más reciente primero)
      despachosValidos.sort((a, b) => new Date(b.hora) - new Date(a.hora));

      setDespachoArray(despachosValidos);
      setShouldFetch(true);
    } catch (error) {
      apiErrorHandler(error, { stopFlow: true });
      setShouldFetch(false);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    //fetchDespachos(); // Load despachos initially
    fetchDespachosPorPuesto();

    const handleResize = () => {
      if (window.innerWidth < 700) {
        setModalWidth("360px");
      } else {
        setModalWidth("900px");
      }
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const useInterval = (callback, delay) => {
    const savedCallback = React.useRef();
    useEffect(() => {
      savedCallback.current = callback;
    }, [callback]);

    useEffect(() => {
      function tick() {
        savedCallback.current();
      }
      if (delay !== null) {
        const id = setInterval(tick, delay);
        return () => clearInterval(id);
      }
    }, [delay]);
  };
  useInterval(() => {
    if (shouldFetch) {
      //fetchDespachos(); // Solo ejecuta si shouldFetch es true
      fetchDespachosPorPuesto();
    }
  }, tiempoDeRefresco);

  const fetchDespachos = async () => {
    setIsLoading(true);
    try {
      const despachos = await Despachos.getDespachos();
      const despachosData = Object.values(despachos.data);
      despachosData.sort((a, b) => {
        const dateA = new Date(a.fecha);
        const dateB = new Date(b.fecha);
        return dateB - dateA;
      });
      setDespachoArray(despachosData);
      setShouldFetch(true); // Permitir futuras llamadas si no hay errores
    } catch (error) {
      apiErrorHandler(error, { stopFlow: true });
      setShouldFetch(false); // Detener futuras llamadas
    } finally {
      setIsLoading(false);
    }
  };

  const handleRowClick = async despacho => {
    if (window.location.pathname === "/dashboard") {
    } else {
      navigate("/dashboard");
    }
    console.log(despacho);

    // Crear una copia del objeto despacho antes de modificarlo
    const despachoModificado = {
      ...despacho,
      idProductoArray: idProductoArray,
      esDespacho: true,
    };

    const despachosArray = JSON.parse(localStorage.getItem("despachosSeleccionados")) || [];
    despachosArray.push(despacho.idDespacho);
    localStorage.setItem("despachosSeleccionados", JSON.stringify(despachosArray));

    setIsLoading(true);

    try {
      await estadoDespacho.actualizarDespacho(despacho.idDespacho, 1, 0);
      addProducto(despachoModificado);
    } catch (error) {
      apiErrorHandler(error, { stopFlow: true });
    }
  };

  const handleCancelar = () => {
    window.history.back();
  };

  const handleAutoservicioClick = () => {
    navigate("/autoservicioPage");
  };

  return (
    <>
      <MenuSection />
      <Box
        sx={{
          position: "absolute",
          width: modalWidth,
          top: window.innerWidth < 700 ? "52%" : "55%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          backgroundColor: "white",
          outline: "none",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Paper
          sx={{
            height: window.innerWidth < 700 ? 437 : 345,
            overflow: "auto",
            "&::-webkit-scrollbar": {
              width: 6,
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "#999",
              borderRadius: 2,
            },
            flexGrow: 1,
            boxShadow: 0,
          }}
        >
          <Table>
            <TableHead>
              <TableRow>
                <TableCell
                  align="center"
                  sx={{
                    position: "sticky",
                    top: 0,
                    zIndex: 1,
                    backgroundColor: theme.bandera.colorSecundario,
                    color: colorTexto,
                    fontWeight: "bold",
                    borderBottom: "2px solid #fff",
                    padding: "3px",
                    fontSize: window.innerWidth > 700 ? "17px" : "11px",
                  }}
                >
                  SURT.
                </TableCell>
                <TableCell
                  align="center"
                  sx={{
                    position: "sticky",
                    top: 0,
                    zIndex: 1,
                    backgroundColor: theme.bandera.colorSecundario,
                    color: colorTexto,
                    fontWeight: "bold",
                    borderBottom: "2px solid #fff",
                    padding: "3px",
                    fontSize: window.innerWidth > 700 ? "17px" : "11px",
                  }}
                >
                  MANG.
                </TableCell>
                <TableCell
                  align="center"
                  sx={{
                    position: "sticky",
                    top: 0,
                    zIndex: 1,
                    backgroundColor: theme.bandera.colorSecundario,
                    color: colorTexto,
                    fontWeight: "bold",
                    borderBottom: "2px solid #fff",
                    padding: "3px",
                    fontSize: window.innerWidth > 700 ? "17px" : "11px",
                  }}
                >
                  PROD.
                </TableCell>
                <TableCell
                  align="center"
                  sx={{
                    position: "sticky",
                    top: 0,
                    zIndex: 1,
                    backgroundColor: theme.bandera.colorSecundario,
                    color: colorTexto,
                    fontWeight: "bold",
                    borderBottom: "2px solid #fff",
                    padding: "3px",
                    fontSize: window.innerWidth > 700 ? "17px" : "11px",
                  }}
                >
                  LITROS
                </TableCell>
                <TableCell
                  align="center"
                  sx={{
                    position: "sticky",
                    top: 0,
                    zIndex: 1,
                    backgroundColor: theme.bandera.colorSecundario,
                    color: colorTexto,
                    fontWeight: "bold",
                    borderBottom: "2px solid #fff",
                    padding: "3px",
                    fontSize: window.innerWidth > 700 ? "17px" : "11px",
                  }}
                >
                  IMPORTE
                </TableCell>
              </TableRow>
            </TableHead>
            {isLoading ? (
              <TableBody>
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ borderBottom: "none" }}>
                    <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", paddingTop: "8rem" }}>
                      <CircularProgress sx={{ color: theme.bandera.colorSecundario }} />
                    </Box>
                  </TableCell>
                </TableRow>
              </TableBody>
            ) : (
              <TableBody>
                {despachoArray.slice(0, cantidadAMostrar).map(despacho => {
                  const cssColor = ""; // Puedes usar argbToCssColor si tu backend lo provee
                  return (
                    <TableRow
                      key={despacho.idDespacho}
                      sx={{
                        textDecoration: "none",
                        "&:hover": {
                          backgroundColor: "rgba(0, 0, 0, 0.1)",
                          cursor: "pointer",
                        },
                        height: "80px",
                        userSelect: "none",
                      }}
                      onClick={() => handleRowClick(despacho)}
                    >
                      <TableCell align="center" sx={{ padding: "5px", fontSize: "14px", backgroundColor: cssColor }}>
                        {despacho.surtidor}
                      </TableCell>
                      <TableCell align="center" sx={{ padding: "5px", fontSize: "14px", backgroundColor: cssColor }}>
                        {despacho.manguera}
                      </TableCell>
                      <TableCell align="center" sx={{ padding: "5px", fontSize: "12px", backgroundColor: cssColor }}>
                        {despacho.producto}
                      </TableCell>
                      <TableCell align="center" sx={{ padding: "5px", fontSize: "14px", backgroundColor: cssColor }}>
                        {despacho.litros.toFixed(2)}
                      </TableCell>
                      <TableCell align="center" sx={{ padding: "5px", fontSize: "14px", backgroundColor: cssColor }}>
                        ${despacho.importe.toFixed(2)}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            )}
          </Table>
        </Paper>
        <Box
          sx={{
            display: "flex",
            justifyContent: esYPF ? "space-between" : "flex-end",
            padding: "20px 10px",
          }}
        >
          {esYPF && (
            <Button
              variant="contained"
              color="primary"
              size="small"
              // startIcon={<LocalGasStationIcon />}
              onClick={handleAutoservicioClick}
              sx={{
                //marginLeft: "8px",
                boxShadow: 0,
                fontWeight: "bold",
                fontSize: 17,
                backgroundColor: theme.bandera.colorSecundario,
                color: colorTexto,
                ":hover": {
                  backgroundColor: theme.bandera.colorSecundario,
                },
              }}
            >
              Autoservicio
            </Button>
          )}
          <Button
            variant="contained"
            sx={{
              boxShadow: 0,
              fontWeight: "bold",
              fontSize: 17,
              backgroundColor: theme.bandera.colorSecundario,
              color: colorTexto,
              ":hover": {
                backgroundColor: theme.bandera.colorSecundario,
              },
            }}
            onClick={() => handleCancelar()}
          >
            CANCELAR
          </Button>
        </Box>
      </Box>
    </>
  );
};

export default DespachosSearchPage;
